/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable jsx-a11y/label-has-associated-control */
import { F, Icon, Button, Flex } from 'nallian-shared-ui/lib/components';
import { PropTypes } from 'prop-types';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useDebouncedCallback } from 'use-debounce';
import identitiesCompleteService from '../../../../app/services/IdentitiesCompleteService';

const Wrapper = styled.div`
  & {
    :focus-within {
      .ui.input input,
      .ui.input input:focus,
      .ui.selection.dropdown {
        border-color: #85b7d9;
        color: rgba(0, 0, 0, 0.95);
      }
    }
    display: flex;
    flex-flow: row;
    width: 100%;
    div:first-child {
      width: unset;
      input {
        min-width: 9rem;
      }
    }
    .field {
      flex-grow: 1;
      margin: 0 !important;
    }
  }
`;

// Handle search shows all records as received. We need this func otherwise the component filters the results
const handleSearch = r => r;

const createIdentityOption = p => ({
  key: p.id,
  value: p.id,
  text: `${p.fullName} - (${
    p.emailAddress && p.phoneNumber
      ? `${p.emailAddress} / ${p.phoneNumber}`
      : p.emailAddress
      ? `${p.emailAddress}`
      : `${p.phoneNumber}`
  } - ${p.identityProvider?.name})`,
});

const IdentitiesDropdown = ({
  disabled,
  dispatchIdentitiesCompleteGet,
  identitiesCompleteFetch,
  input,
  dispatchChange,
  meta,
  onChange,
  loading,
  ...props
}) => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [principalId, setPrincipalId] = useState();
  const debouncedDispatch = useDebouncedCallback(dispatchIdentitiesCompleteGet, 400, {
    leading: true,
  });

  const identitiesCompleteSelectOptions = useMemo(
    () => (identitiesCompleteFetch.value || []).map(createIdentityOption),
    [identitiesCompleteFetch.value]
  );

  useEffect(() => {
    if (searchQuery?.length >= 3) debouncedDispatch({ query: searchQuery }, t);
  }, [searchQuery, debouncedDispatch, t]);

  const handleSearchChange = useCallback(
    (_, { searchQuery: query }) => {
      setSearchQuery(query);
      setPrincipalId(undefined);
    },
    [setSearchQuery, setPrincipalId]
  );

  const handleSearchFocus = useCallback(() => setSearchQuery(''), [setSearchQuery]);

  const handleChange = useCallback(
    (_, data) => {
      const option = (data?.options || []).find(o => o.id === data?.value);
      setPrincipalId(data?.value);
      setSearchQuery(option?.text);
    },
    [setSearchQuery, setPrincipalId]
  );

  const handleSubmit = useCallback(() => {
    onChange(principalId || searchQuery, () => {
      setSearchQuery('');
      setPrincipalId(undefined);
    });
  }, [setSearchQuery, principalId, searchQuery, onChange]);

  return (
    <Wrapper>
      <div className="ui field">
        <label htmlFor="identityId">{t('searchForAnIdentityOrAddClientId')}</label>
        <Flex>
          <F.Select
            id="identityId"
            labelPosition="left"
            value={!searchQuery ? principalId : input?.value}
            onChange={handleChange}
            name={input?.name}
            options={identitiesCompleteSelectOptions}
            onSearchChange={handleSearchChange}
            onFocus={handleSearchFocus}
            searchQuery={searchQuery}
            loading={identitiesCompleteFetch.pending}
            noResultsMessage={!identitiesCompleteFetch.pending ? t('noUsersFound') : ''}
            icon={<Icon name="search" size="small" margin="-0.45rem -0.75rem" />}
            disabled={disabled}
            minCharacters={2}
            fluid
            clearable
            selectOnNavigation={false}
            selectOnBlur={false}
            search={handleSearch}
            lazyLoad
            {...props}
          />
          <Button
            margin="0 0 0 0.5rem"
            icon="add"
            content={t('add')}
            onClick={handleSubmit}
            disabled={disabled || loading}
            loading={loading}
            btnprimary
          />
        </Flex>
      </div>
    </Wrapper>
  );
};

IdentitiesDropdown.propTypes = {
  identitiesCompleteFetch: PropTypes.object.isRequired,
  dispatchIdentitiesCompleteGet: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  input: PropTypes.object.isRequired,
  dispatchChange: PropTypes.func.isRequired,
  meta: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
};

IdentitiesDropdown.defaultProps = { disabled: false };

export default identitiesCompleteService(IdentitiesDropdown);
